import type { ReactNode } from 'react';

export type AdminMetadata = {
  description?: string;
  icons?: Array<{
    media?: string;
    rel?: string;
    sizes?: string;
    type?: string;
    url: string;
  }>;
  keywords?: string;
  openGraph?: {
    description?: string;
    images?: Array<{
      alt?: string;
      height?: number;
      url: string;
      width?: number;
    }>;
    siteName?: string;
    title?: string;
  };
  robots?: string;
  title?: string;
  viewport?: string;
};

export type AdminRouteData = {
  _notFound?: boolean;
  _redirect?: string;
  metadata?: AdminMetadata;
  routeKey?: string;
  rscPayload?: ReactNode;
};

type HeadMeta = {
  charSet?: string;
  content?: string;
  name?: string;
  property?: string;
  title?: string;
};
type HeadLink = {
  href: string;
  media?: string;
  rel: string;
  sizes?: string;
  type?: string;
};

export function searchToRecord(search: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(search));
}

export function getAdminHead(metadata?: AdminMetadata): { links: HeadLink[]; meta: HeadMeta[] } {
  const meta: HeadMeta[] = [
    { charSet: 'utf-8' },
    { name: 'viewport', content: metadata?.viewport ?? 'width=device-width, initial-scale=1' },
  ];
  const links: HeadLink[] = [];

  if (!metadata) return { links, meta };
  if (metadata.title) meta.push({ title: metadata.title });
  if (metadata.description) meta.push({ name: 'description', content: metadata.description });
  if (metadata.keywords) meta.push({ name: 'keywords', content: metadata.keywords });
  if (metadata.robots) meta.push({ name: 'robots', content: metadata.robots });

  const openGraph = metadata.openGraph;
  if (openGraph) {
    if (openGraph.title) meta.push({ property: 'og:title', content: openGraph.title });
    if (openGraph.description) {
      meta.push({ property: 'og:description', content: openGraph.description });
    }
    if (openGraph.siteName) meta.push({ property: 'og:site_name', content: openGraph.siteName });

    for (const image of openGraph.images ?? []) {
      meta.push({ property: 'og:image', content: image.url });
      if (image.width) meta.push({ property: 'og:image:width', content: String(image.width) });
      if (image.height) meta.push({ property: 'og:image:height', content: String(image.height) });
      if (image.alt) meta.push({ property: 'og:image:alt', content: image.alt });
    }

    const firstImage = openGraph.images?.[0];
    if (firstImage) {
      meta.push({ name: 'twitter:card', content: 'summary_large_image' });
      meta.push({ name: 'twitter:image', content: firstImage.url });
    }
    if (openGraph.title) meta.push({ name: 'twitter:title', content: openGraph.title });
    if (openGraph.description) {
      meta.push({ name: 'twitter:description', content: openGraph.description });
    }
  }

  for (const icon of metadata.icons ?? []) {
    links.push({
      href: icon.url,
      rel: icon.rel ?? 'icon',
      ...(icon.media ? { media: icon.media } : {}),
      ...(icon.sizes ? { sizes: icon.sizes } : {}),
      ...(icon.type ? { type: icon.type } : {}),
    });
  }

  return { links, meta };
}
